import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import PageLayout from '../../../components/dashboard/PageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { buildOrganizationTree } from './organizationTree';
import { RootCard } from './OrganizationCard';
import { ChildrenRow } from './OrganizationNode';

const CUSTOMER_PARAM = 'customer';

function Message({ title, message }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{title}</Typography>
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Stack>
  );
}

export default function OrganizationChart() {
  const { t } = useTranslation();
  const { collaborator, customer: currentCustomer } = useAuth();
  const isInternal = Boolean(collaborator?.is_internal);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleted, setShowDeleted] = useState(false);

  // Internal users pick any customer (deep-linkable); collaborators only
  // ever see their own.
  const customerUuid = isInternal
    ? searchParams.get(CUSTOMER_PARAM) ?? currentCustomer?.uuid ?? null
    : currentCustomer?.uuid ?? null;

  const customersQuery = useQuery({
    queryKey: ['customers'],
    enabled: isInternal,
    retry: retryOnServerError,
    queryFn: () => customerService.getCustomers().then((res) => res.data),
  });

  const customerQuery = useQuery({
    queryKey: ['customers', customerUuid],
    enabled: Boolean(customerUuid),
    retry: retryOnServerError,
    queryFn: () => customerService.getCustomer(customerUuid).then((res) => res.data),
  });

  const businessesQuery = useQuery({
    queryKey: ['businesses', 'organization', customerUuid],
    enabled: Boolean(customerUuid),
    retry: retryOnServerError,
    queryFn: () =>
      customerService.listBusinesses(isInternal ? { customer: customerUuid } : undefined).then((res) => res.data),
  });

  const customerOptions = useMemo(
    () => (customersQuery.data ?? []).filter((item) => showDeleted || !item.is_deleted),
    [customersQuery.data, showDeleted],
  );
  const selectedOption =
    customerOptions.find((item) => item.uuid === customerUuid) ??
    (customerQuery.data?.uuid === customerUuid ? customerQuery.data : null);

  const tree = useMemo(
    () =>
      customerQuery.data &&
      buildOrganizationTree(customerQuery.data, businessesQuery.data ?? [], {
        includeDeleted: isInternal && showDeleted,
      }),
    [customerQuery.data, businessesQuery.data, isInternal, showDeleted],
  );

  const handleCustomerChange = (_event, option) => {
    setSearchParams(option ? { [CUSTOMER_PARAM]: option.uuid } : {}, { replace: true });
  };

  const header = (
    <PageHeader
      title={t('organization.title')}
      breadcrumbs={[
        { label: t('organization.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('organization.title') },
      ]}
    />
  );

  const renderChart = () => {
    if (!customerUuid) {
      return <Message title={t('organization.empty.title')} message={t('organization.empty.message')} />;
    }

    if (customerQuery.isLoading || businessesQuery.isLoading) {
      return <LoadingState message={t('organization.states.loading')} />;
    }

    const failed = [customerQuery, businessesQuery].find((query) => query.isError);
    if (failed) {
      const status = failed.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('organization.states.permissionDeniedTitle')}
            message={t('organization.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState
            title={t('organization.states.notFoundTitle')}
            message={t('organization.states.notFoundMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('organization.states.errorTitle')}
          message={t('organization.states.errorMessage')}
          actionLabel={t('organization.states.retry')}
          onRetry={() => Promise.all([customerQuery.refetch(), businessesQuery.refetch()])}
        />
      );
    }

    const storesCount = (businessesQuery.data ?? []).filter((business) => !business.is_deleted).length;

    return (
      // max-content + minWidth 100%: centred when it fits, scrolls when wide.
      <Box sx={{ width: 'max-content', minWidth: '100%', p: 3 }}>
        <Stack alignItems="center">
          <RootCard
            t={t}
            customer={tree.customer}
            rootStore={tree.rootStore}
            storesCount={storesCount}
            linkToCustomer={isInternal}
          />
          <ChildrenRow t={t} nodes={tree.children} />
          {!tree.children.length && (
            <Message title={t('organization.noStores')} message={tree.rootStore ? null : t('organization.noStoresHint')} />
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <PageLayout header={header}>
      {/* pt: room for the outlined label, which the scrolling body clips. */}
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0, pt: 1, pb: 3 }}>
        {isInternal && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Autocomplete
              sx={{ width: { xs: '100%', sm: 360 } }}
              size="small"
              options={customerOptions}
              value={selectedOption}
              loading={customersQuery.isLoading}
              onChange={handleCustomerChange}
              getOptionLabel={(option) => option.display_name ?? ''}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              renderInput={(params) => <TextField {...params} label={t('organization.selectCustomer')} />}
            />
            <FormControlLabel
              control={<Switch checked={showDeleted} onChange={(event) => setShowDeleted(event.target.checked)} />}
              label={t('organization.showDeleted')}
            />
          </Stack>
        )}
        <Card sx={{ flex: 1, minHeight: 320, overflow: 'auto' }}>
          {renderChart()}
        </Card>
      </Stack>
    </PageLayout>
  );
}
